import { Descriptions, Tag, Typography } from "antd";
import { selectCurrentBusiness } from "../../../businessesSlice";
import { useAppSelector } from "../../../../../app/hooks";
import { BusinessOnlinePricing } from "../../../../../interfaces/onlinePricing.interface";
import { IBusinessFull } from "../../../../../interfaces/businessFull.interface";
import { OnlineBucketKey } from "../../../../partners/constants/onlineBuckets";
import {
  isSixteenBucketMerchant,
  onlineBucketFieldLabel,
  onlineGroupLabel,
} from "../../../utils/sixteenBucketOnlinePricing";
import { hasUneditableOnlinePricing } from "../../../utils/uneditableOnlinePricing";

type Fee = { name: string; value: number; valueType: "Fixed" | "Percentage" };

// Derive the online fee rows from the canonical onlinePricing buckets so this
// view never drifts from the editor / split (the flattened field is a mirror).
const ONLINE_FEE_LABELS: {
  key: OnlineBucketKey;
  name: string;
}[] = [
  { key: "iDeal", name: "iDeal" },
  { key: "bancontact", name: "Bancontact" },
  { key: "amex", name: "AMEX" },
  { key: "corporateCreditCards", name: "Corporate Credit Cards" },
  { key: "nonCorporateCreditCards", name: "Non-Corporate Credit Cards" },
  { key: "appleGooglePay", name: "Apple Pay / Google Pay" },
];

const fourBucketOnlineFees = (
  pricing: BusinessOnlinePricing | undefined
): Fee[] | undefined => {
  if (!pricing) return undefined;
  return ONLINE_FEE_LABELS.flatMap(({ key, name }) => {
    const bucket = pricing[key];
    if (!bucket) return [];
    return [
      {
        name,
        value: bucket.value,
        valueType: bucket.valueType as "Fixed" | "Percentage",
      },
    ];
  });
};

// Last resort when the flattened rows are absent: every group the merchant
// actually stores, named by key where there is no label for it. These are group
// rates rather than per-bucket ones, and a migrated merchant can carry a group
// its split no longer prices — shown anyway, because the alternative reads as
// "No fees configured" on a merchant that is priced and, under the guard above,
// cannot be opened in an editor to check.
const storedOnlineGroupFees = (
  pricing: BusinessOnlinePricing | undefined
): Fee[] | undefined => {
  if (!pricing) return undefined;
  return Object.entries(pricing).flatMap(([key, bucket]) =>
    typeof bucket?.value === "number"
      ? [
          {
            name: onlineGroupLabel(key),
            value: bucket.value,
            valueType: bucket.valueType as "Fixed" | "Percentage",
          },
        ]
      : []
  );
};

// A 16-bucket merchant is priced on groups, so its buckets can't be read off
// onlinePricing by name. The backend already flattens them onto
// onlineMerchantServiceChargeFees under field identifiers — KBM owns the labels.
const onlineFeesFor = (business: IBusinessFull): Fee[] | undefined => {
  // A structure KBM has no group table for reads off the same flattened rows: a
  // legacy key lookup would show only the handful of buckets it can name.
  if (
    !isSixteenBucketMerchant(business) &&
    !hasUneditableOnlinePricing(business)
  ) {
    return fourBucketOnlineFees(business.onlinePricing);
  }
  const flattened = business.onlineMerchantServiceChargeFees;
  // Nothing guarantees the mirror exists: on dev most four-bucket merchants
  // carry onlinePricing with no flattened rows at all.
  if (!flattened?.length) return storedOnlineGroupFees(business.onlinePricing);
  return flattened.map((fee) => ({
    name: onlineBucketFieldLabel(fee.name),
    value: fee.value,
    valueType: fee.valueType,
  }));
};

function FeeSection({
  title,
  fees,
}: {
  title: string;
  fees: Fee[] | undefined;
}) {
  return (
    <div>
      <Typography.Title level={5} style={{ marginBottom: 12 }}>
        {title}
      </Typography.Title>
      {!fees || fees.length === 0 ? (
        <Typography.Text type="secondary" italic>
          No fees configured
        </Typography.Text>
      ) : (
        <Descriptions labelStyle={{ opacity: 0.6, width: "130px" }}>
          {fees.map((f) => (
            <Descriptions.Item key={f?.name} label={f?.name}>
              {f?.value}
              {f?.valueType === "Percentage" ? (
                "%"
              ) : (
                <Tag style={{ marginLeft: "8px" }}>FIXED</Tag>
              )}
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}
    </div>
  );
}

export default function BusinessServiceFees() {
  const currentBusiness = useAppSelector(selectCurrentBusiness);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <FeeSection
        title="In Person Payments Fees"
        fees={currentBusiness.merchantServiceChargeFees}
      />
      <FeeSection
        title="Online Payments Fees"
        fees={onlineFeesFor(currentBusiness)}
      />
    </div>
  );
}
